import { CoreContainer, CoreContainerContext } from '@hub/core-container'
import { Stack } from '@hub/stack'
import { H3 } from '@hub/heading'
import { Text } from '@hub/text'
import { Cluster } from '@hub/cluster'
import { Link } from '@hub/link'
import React, { useRef, useEffect, useState, useContext } from 'react'
import { Reel, RenderControls } from '@hub/reel'
import { IncludeDirectLogo } from '@hub/icon/src/with-direct-logo'
import { Imposter } from '@hub/imposter'
import { ArrowLeftSmallIcon, ArrowRightSmallIcon } from '@hub/icon'
import { IconButton } from '@hub/button'
import {
  useToken,
  forwardRef,
  DEFAULT_BREAKPOINT,
  useBreakpointValue,
} from '@hub/design-system-base'
import type { As, StandardSizes, HubStyleObject } from '@hub/design-system-base'
import debounce from 'lodash/debounce'

import { SGPageTemplateProduct } from '../../../../src/types/page-templates'
import { SGFeaturedLink } from '../../../types/featured-link'
import { ProductCard } from '../product-card'

export type Product = React.ComponentProps<typeof ProductCard> & { id: string }

interface ProductDeckProps {
  title: string
  text?: string
  headerLinks?: SGFeaturedLink[]
  products: SGPageTemplateProduct[]
}

interface ReelButtonProps {
  sx?: HubStyleObject
  onClick: React.MouseEventHandler
  icon: React.ComponentProps<typeof IconButton>['icon']
  'aria-label': string
  as?: As
}

const buttonSizeToken = 'size-8'

export const ReelButton = forwardRef<ReelButtonProps, typeof IconButton>(
  ({ sx, onClick, icon, as, ...rest }, ref) => (
    <IconButton
      ref={ref}
      aria-label={rest['aria-label']}
      sx={{
        borderRadius: '50%',
        boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.15)',
        width: buttonSizeToken,
        height: buttonSizeToken,
        minWidth: buttonSizeToken,
        ...sx,
      }}
      icon={icon}
      as={as}
      colorScheme="white"
      variant="solid"
      aria-hidden="true"
      onClick={onClick}
    />
  )
)

const renderReelControls: ({
  buttonSize,
  buttonOffsetY,
  buttonAlignmentX,
}: {
  buttonSize: StandardSizes
  buttonOffsetY: string
  buttonAlignmentX: 'edge' | 'center'
}) => // eslint-disable-next-line react/display-name
RenderControls = ({ buttonSize, buttonOffsetY, buttonAlignmentX }) => ({
  hasOverflowingFrames,
  children,
  nextComponentProps,
  prevComponentProps,
  scrollPosition,
}) =>
  hasOverflowingFrames ? (
    <Imposter
      impose={
        <ReelButton
          aria-label="Scroll to next"
          sx={{
            display: ['none', 'inherit'],
            visibility: scrollPosition === 'end' ? 'hidden' : 'visible',
          }}
          {...nextComponentProps}
          icon={<ArrowRightSmallIcon />}
        />
      }
      position="top-right"
      marginTop={`calc(${buttonOffsetY} - ${buttonSize} / 2)`}
      marginRight={
        buttonAlignmentX === 'center' ? `calc(${buttonSize} / -2)` : undefined
      }
    >
      <Imposter
        impose={
          <ReelButton
            aria-label="Scroll to previous"
            sx={{
              display: ['none', 'inherit'],
              visibility: scrollPosition === 'start' ? 'hidden' : 'visible',
            }}
            {...prevComponentProps}
            icon={<ArrowLeftSmallIcon />}
          />
        }
        position="top-left"
        marginTop={`calc(${buttonOffsetY} - ${buttonSize} / 2)`}
        marginLeft={
          buttonAlignmentX === 'center' ? `calc(${buttonSize} / -2)` : undefined
        }
      >
        {children}
      </Imposter>
    </Imposter>
  ) : (
    children
  )

export const ProductDeckReel: React.FC<{
  products: SGPageTemplateProduct[]
}> = ({ products }) => {
  const buttonAlignmentX = useBreakpointValue(
    ['edge', 'edge', 'center'],
    DEFAULT_BREAKPOINT
  ) as 'edge' | 'center'
  const buttonSize = useToken('sizes', buttonSizeToken)
  const container = useContext(CoreContainerContext)
  const [buttonOffsetY, setButtonOffsetY] = useState<string>('50%')
  const elementButtonRelativeTo = useRef<HTMLElement>(null)

  // NOTE: not using useLayoutEffect because we don't want to block rendering
  // just to move the button slightly
  useEffect(() => {
    setButtonOffsetY(
      `${Math.floor(
        // eslint-disable-next-line no-magic-numbers
        (elementButtonRelativeTo?.current?.clientHeight ?? 0) / 2
      )}px`
    )

    // On some feature phones, ResizeObserver is not supported. They can just
    // stick with the above calculation instead of worrying about the resize
    // events
    // ie; avoid "ResizeObserver is not supported in KaiOS 2.5, Baidu 7.12,
    // Android UC Browser 12.12"
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!window.ResizeObserver) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}
    }

    const resizeHandler = debounce(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line compat/compat
      (entries: ResizeObserverEntry[]) => {
        // eslint-disable-next-line no-magic-numbers
        setButtonOffsetY(`${Math.floor(entries[0].contentRect.height / 2)}px`)
      },
      100,
      // Execute immediately when scrolling starts, then debounce thereafter
      { leading: true, trailing: true }
    )

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line compat/compat
    const resizeObserver = new ResizeObserver(resizeHandler)

    if (elementButtonRelativeTo?.current) {
      resizeObserver.observe(elementButtonRelativeTo?.current)
    }

    return () => {
      // Cancel any pending debounced method calls
      resizeHandler.cancel()
      // Stop observing resize events entirely
      resizeObserver.disconnect()
    }
  }, [])

  /* eslint-disable no-magic-numbers */
  return (
    <Reel
      snap
      gap={['spacing-sm', null, 'spacing-md']}
      visibleFrames={[2, 3, 4, 5]}
      containerOverflow={[container.gutter, null, null, 'spacing-none']}
      peekWidth={[0.1, 0]}
      shouldWrapChildren={false}
      renderControls={renderReelControls({
        buttonSize,
        buttonOffsetY,
        buttonAlignmentX,
      })}
    >
      {products.map((product, index) => (
        <ProductCard
          key={`${product.variantGroupCode}-${product.shop.name}`}
          // Capture the ref for the first element's image
          imageRef={index === 0 ? elementButtonRelativeTo : null}
          product={product}
        />
      ))}
    </Reel>
  )
  /* eslint-enable no-magic-numbers */
}

export const ProductDeck: React.FC<ProductDeckProps> = ({
  title,
  text,
  headerLinks = [],
  products,
}) => (
  <CoreContainer>
    <Stack gap="spacing-sm">
      <Stack gap="spacing-none">
        <H3 align="center">
          <IncludeDirectLogo title={title} />
        </H3>
        {text ? (
          <Text align="center" color="quartz">
            {text}
          </Text>
        ) : undefined}
        {headerLinks?.length > 0 ? (
          <Cluster justify="center" gapX="spacing-lg">
            {headerLinks.map((link, i) => (
              <Text key={i} decoration="underline" lineHeight="base">
                <Link href={link.url}>{link.title}</Link>
              </Text>
            ))}
          </Cluster>
        ) : undefined}
      </Stack>
      <ProductDeckReel products={products} />
    </Stack>
  </CoreContainer>
)
